/**
 * „Poslat poptávku" — the way out of the cart for shops that do not
 * take card payments.
 *
 * Many workshops sell by agreeing the details first and invoicing by
 * transfer. For them a „Zaplatit kartou" button is not a feature but
 * the reason they never deploy the configurator at all.
 *
 * Shown per the tenant's ``business_info.checkout_mode``. In ``both``
 * it sits under the payment button as the secondary option; in
 * ``inquiry`` it is the only way out of the cart.
 *
 * The enquiry is not a new entity — the server turns the cart the
 * customer already built into a ``ContactMessage`` in the shop's
 * existing Contact Inbox.
 */

import { Button } from "@/ingot";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSubmitCartInquiry } from "@/api/queries/cart";

import { ApiError } from "@/lib/api";

export default function CartInquiryButton({
  variant,
}: {
  /** ``primary`` when it is the only way out of the cart. */
  variant: "primary" | "secondary";
}): JSX.Element {
  const { t } = useTranslation("cart");
  const submit = useSubmitCartInquiry();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  if (sent) {
    return (
      <p
        className="mt-4 rounded-md border border-border bg-surface p-3 text-sm text-ink"
        data-testid="cart-inquiry-sent"
        role="status"
      >
        {t("inquiry.sent")}
      </p>
    );
  }

  if (!open) {
    return (
      <Button
        variant={variant === "primary" ? "accent" : "ghost"}
        size={variant === "primary" ? "lg" : "md"}
        className="mt-2 w-full"
        onClick={() => setOpen(true)}
        data-testid="cart-inquiry-open"
      >
        {t("inquiry.cta")}
      </Button>
    );
  }

  return (
    <form
      className="mt-4 space-y-2"
      data-testid="cart-inquiry-form"
      onSubmit={(e) => {
        e.preventDefault();
        setErrorCode(null);
        submit.mutate(
          { name, email, phone: phone || null, message: message || null },
          {
            onSuccess: () => setSent(true),
            onError: (err) =>
              setErrorCode(err instanceof ApiError ? err.code : "unknown"),
          },
        );
      }}
    >
      <p className="text-sm text-ink-2">{t("inquiry.lead")}</p>
      <input
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t("inquiry.name")}
        aria-label={t("inquiry.name")}
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink"
      />
      <input
        required
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("inquiry.email")}
        aria-label={t("inquiry.email")}
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink"
      />
      <input
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder={t("inquiry.phone")}
        aria-label={t("inquiry.phone")}
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink"
      />
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t("inquiry.message")}
        aria-label={t("inquiry.message")}
        rows={3}
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink"
      />
      {errorCode && (
        <p className="text-sm text-danger" role="alert" data-testid="cart-inquiry-error">
          {t(`inquiry.errors.${errorCode}`, {
            defaultValue: t("inquiry.errors.unknown"),
          })}
        </p>
      )}
      <Button
        type="submit"
        variant="accent"
        size="lg"
        className="w-full"
        disabled={submit.isPending}
        data-testid="cart-inquiry-submit"
      >
        {t("inquiry.submit")}
      </Button>
    </form>
  );
}
